import React, { useState, useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SplitFactory } from '@splitsoftware/splitio';

import splitConfig, { SPLIT_ENABLED } from './splitConfig';
import controlledTreatments, { DEFAULT_TREATMENT } from './controlledTreatments';
import { datadogRum } from '@datadog/browser-rum-slim';

export const FeatureFlagContext = React.createContext();

const timeoutForSplitInMSec = 3000;

export const useFeatureFlag = () => useContext(FeatureFlagContext);

// Init Split Client.
let splitClient;
if (SPLIT_ENABLED) {
  const factory = SplitFactory(splitConfig);
  splitClient = factory.client();
}

const FeatureFlagProvider = ({ children }) => {
  const [client] = useState(splitClient);
  const [isReady, setIsReady] = useState(false);
  const [fallbackToControlledTreat, setFallbackToControlledTreat] = useState(!SPLIT_ENABLED);
  const [flagCache, setFlagCache] = React.useState({});

  useEffect(() => {
    // If Split is not enabled then fall back to controlled treatments
    if (!SPLIT_ENABLED) {
      setFallbackToControlledTreat(true);
      setIsReady(true);
      return () => {};
    }

    let isMounted = true;
    let splitTimeoutPointer;

    const promiseSplit = new Promise((resolve, reject) => {
      splitClient.on(splitClient.Event.SDK_READY, () => {
        resolve();
      });
      splitClient.once(splitClient.Event.SDK_READY_FROM_CACHE, () => {
        // Split from local browser storage will always resolve faster
        // but if it is not set (initial load)
        // we have to wait for remote fetch to be ready
        resolve();
      });

      splitTimeoutPointer = setTimeout(() => {
        reject(new Error('SplitTimeout'));
      }, timeoutForSplitInMSec);
    });

    promiseSplit
      .then(() => {
        if (isMounted) {
          setIsReady(true);
        }
      })
      .catch((e) => {
        setFallbackToControlledTreat(true);
        setIsReady(true);
        datadogRum.addError(e);
      });

    return () => {
      isMounted = false;
      clearTimeout(splitTimeoutPointer);
    };
  }, []);

  const updateCache = useCallback(
    (name, treatment) => {
      setFlagCache((oldCache) => ({ ...oldCache, ...{ [name]: treatment } }));
    },
    [setFlagCache],
  );

  const getTreatment = useCallback(
    (name) => {
      if (!controlledTreatments[name]) {
        // use controlledTreatments as "validation"
        // to check if flag name is defined in Split
        return DEFAULT_TREATMENT;
      }

      // read treatment from cache
      let treatment = flagCache[name]; // default: undefined

      if (!treatment) {
        if (fallbackToControlledTreat) {
          // fall back to controleld treatment
          // if Split is turned off globally, or
          // if Split failed to start
          treatment = controlledTreatments[name];
        } else {
          // fetch remotely
          treatment = client.getTreatmentWithConfig(name);
        }

        // update cache
        updateCache(name, treatment);
      }

      return treatment;
    },
    [client, fallbackToControlledTreat, flagCache, updateCache],
  );

  useEffect(() => {
    splitClient.on(splitClient.Event.SDK_UPDATE, () => {
      // listen to Split change
      // and update local cache
      const flagNames = Object.keys(controlledTreatments);
      flagNames.forEach((flagName) => getTreatment(flagName));
    });
  }, [getTreatment]);

  const isTreatmentOn = useCallback(
    (name) => {
      const resolvedTreatment = getTreatment(name);
      const treatmentValue = resolvedTreatment && resolvedTreatment.treatment;
      return treatmentValue === 'on';
    },
    [getTreatment],
  );

  return (
    <FeatureFlagContext.Provider
      value={{
        isReady,
        getTreatment,
        isTreatmentOn,
        flagCache,
      }}
    >
      <>{children}</>
    </FeatureFlagContext.Provider>
  );
};

FeatureFlagProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default FeatureFlagProvider;
